import type {
  ChangeEventHandler,
  CSSProperties,
  FocusEventHandler,
  InputHTMLAttributes,
  ReactNode,
  Ref,
} from 'react';
import type { WebsocketAwarenessState } from '../../hooks/useWebsocket/websocket';
import type { SlugRule } from '../../lib/flotiq-client/types/api-response';
import type { HelpErrorTextsProps } from '../HelpErrorTextsTemplate/HelpErrorTextsTemplate';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../lib/helpers';

import { EyeCrossedIcon, EyeIcon, MagnifierIcon } from '../../images/shapes';

import FieldEditorsIndicator from '../FieldEditorsIndicator/FieldEditorsIndicator';
import HelpErrorTextsTemplate from '../HelpErrorTextsTemplate/HelpErrorTextsTemplate';
import { PasswordValidationBox } from '../PasswordValidationBox/PasswordValidationBox';
import RequiredTemplate from '../RequiredTemplate/RequiredTemplate';

export interface InputProps
  extends InputHTMLAttributes<HTMLInputElement>, HelpErrorTextsProps {
  /**
   * React refs to input
   */
  refs?: Ref<HTMLInputElement>;
  /**
   * Input label
   */
  label?: ReactNode;
  /**
   * Input size of the input
   */
  inputSize?: string;
  /**
   * If input is readonly (non-interactive but without disabled classes)
   */
  readonly?: boolean;
  /**
   * If information about password validation should be shown
   */
  showPasswordInfo?: boolean;
  /**
   * Input additional classes
   */
  additionalClasses?: string;
  /**
   * Input additional input classes
   */
  additionalInputClasses?: string;
  /**
   * Input additional label classes
   */
  additionalInputLabelClasses?: string;
  /**
   * Input additional error classes
   */
  additionalErrorClasses?: string;
  /**
   * Checkbox on type search if we want to show icon
   */
  isSearchIcon?: boolean;
  /**
   * Input password-like not sensitive to password managers
   */
  notSensitiveToPasswordManager?: boolean;
  /**
   * Input get visual separate slash preview when value include slash "/"
   */
  visualSeparateSlash?: boolean;
  /**
   * If current value should be updated if is different than passed value
   */
  updateCurrent?: boolean;
  /**
   * Additional classes for Eye and EyeCrossed icon while input is type password
   */
  passwordInputIconAdditionalClasses?: string;
  /**
   * Editors data who are currently editing the field
   */
  editors?: WebsocketAwarenessState[];
  /**
   * Field slugger rules
   */
  slugRules?: SlugRule;
  /**
   * Handle slug creation on blur event, if slugger rules are active and value is empty
   */
  handleSlugCreationOnBlur?: () => void;
  /**
   * Checkbox test id
   */
  testId?: string;
}

const Input = ({
  value = '',
  type = 'text',
  required = false,
  onChange,
  onBlur,
  onFocus,
  name = '',
  label = '',
  inputSize = '',
  error = '',
  disabled = false,
  readonly = false,
  helpText = '',
  showPasswordInfo = false,
  additionalClasses = '',
  additionalInputClasses = '',
  additionalInputLabelClasses = '',
  additionalErrorClasses = '',
  testId = '',
  isSearchIcon = true,
  refs,
  autoFocus,
  visualSeparateSlash = false,
  notSensitiveToPasswordManager = false,
  updateCurrent = false,
  passwordInputIconAdditionalClasses = '',
  editors,
  ...props
}: InputProps) => {
  const ref = useRef<HTMLInputElement>(null);
  const [currentValue, setCurrentValue] = useState(value);
  const [currentType, setCurrentType] = useState(
    notSensitiveToPasswordManager ? 'text' : type,
  );
  const [isFocused, setIsFocused] = useState(false);
  const [isNotSensitiveToPasswordManager, setIsNotSensitiveToPasswordManager] =
    useState(notSensitiveToPasswordManager);

  const apiKeyInputStyle = useMemo(() => {
    if (isNotSensitiveToPasswordManager) {
      return {
        textSecurity: 'disc',
        WebkitTextSecurity: 'disc',
        MozTextSecurity: 'disc',
      };
    }
    return {};
  }, [isNotSensitiveToPasswordManager]);

  useEffect(() => {
    if (currentType !== 'password' && type !== 'password') {
      setCurrentType(type);
    }
    setIsNotSensitiveToPasswordManager(notSensitiveToPasswordManager);
  }, [currentType, type, notSensitiveToPasswordManager]);

  useEffect(() => {
    if (autoFocus) {
      ref.current?.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  useEffect(() => {
    if (updateCurrent && value !== currentValue) setCurrentValue(value);
  }, [currentValue, value, updateCurrent]);

  const handleChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
    (e) => {
      setCurrentValue(e.target.value);
      onChange?.(e);
    },
    [onChange],
  );

  const handlePasswordVisibility = useCallback(() => {
    if (notSensitiveToPasswordManager) {
      if (isNotSensitiveToPasswordManager) {
        setIsNotSensitiveToPasswordManager(false);
      } else {
        setIsNotSensitiveToPasswordManager(true);
      }
      return;
    }

    if (currentType === 'password') {
      setCurrentType('text');
    } else {
      setCurrentType('password');
    }
  }, [
    notSensitiveToPasswordManager,
    currentType,
    isNotSensitiveToPasswordManager,
  ]);

  const handleBlur = useCallback<FocusEventHandler<HTMLInputElement>>(
    (e) => {
      setIsFocused(false);
      if (onBlur) onBlur(e);
    },
    [onBlur],
  );

  const handleFocus = useCallback<FocusEventHandler<HTMLInputElement>>(
    (e) => {
      setIsFocused(true);
      onFocus?.(e);
    },
    [onFocus],
  );

  const handleSeparateVisualSlash = useCallback(() => {
    if (
      typeof currentValue === 'string' &&
      currentValue?.indexOf('/') > -1 &&
      currentValue?.indexOf('/') !== 0 &&
      currentValue?.indexOf('/') === currentValue.lastIndexOf('/')
    ) {
      const folderName = currentValue.split('/')?.[0];
      return (
        <div
          className={twMerge(
            'absolute z-10 pl-2 left-1 bottom-1 flex justify-start items-center',
            'overflow-hidden -mt-[10px] ',
            'bg-white dark:bg-slate-950 pointer-events-none max-w-full',
            disabled && 'bg-slate-100',
          )}
          style={{ width: 'calc(100% - 10px)', height: 'calc(100% - 10px)' }}
          {...getTestProps(testId, 'separate-visual-slash-container')}
        >
          <span
            className={twMerge(
              'bg-blue-300 text-white py-[6px] px-[12px]',
              'rounded-[6px] text-slate-700 whitespace-nowrap',
              'overflow-hidden text-ellipsis',
              folderName.length > 20 && 'min-w-[100px]',
            )}
            {...getTestProps(testId, 'separate-visual-slash-folder-name')}
          >
            {folderName}
          </span>
          <span className="mx-1 dark:text-white">/</span>
          <span
            className="whitespace-nowrap dark:text-white"
            {...getTestProps(testId, 'separate-visual-slash-rest-name')}
          >
            {currentValue.split('/')?.[1]}
          </span>
        </div>
      );
    }
  }, [currentValue, disabled, testId]);

  return (
    <div
      className={twMerge(
        'w-full',
        'flex',
        'flex-col',
        'relative',
        additionalClasses,
      )}
      style={{ width: inputSize }}
      {...getTestProps(testId, 'container')}
    >
      {label && (
        <label
          className={twMerge(
            'text-sm text-slate-400 dark:text-slate-200 mb-1',
            additionalInputLabelClasses,
          )}
          {...getTestProps(testId, 'label')}
        >
          {label}
          {required && <RequiredTemplate />}
        </label>
      )}
      {!label && required && (
        <RequiredTemplate absolute={true} isIcon={type === 'password'} />
      )}
      {(type === 'password' || notSensitiveToPasswordManager) && (
        <>
          {currentType === 'password' || isNotSensitiveToPasswordManager ? (
            <EyeCrossedIcon
              onClick={handlePasswordVisibility}
              className={twMerge(
                'w-5 cursor-pointer',
                'absolute right-5 z-10 text-slate-400/80 dark:text-slate-200',
                label ? 'top-[33px]' : 'top-[14px]',
                passwordInputIconAdditionalClasses,
              )}
              {...getTestProps(testId, 'password-toggle')}
            />
          ) : (
            <EyeIcon
              onClick={handlePasswordVisibility}
              className={twMerge(
                'w-5 cursor-pointer',
                'absolute right-5 z-10 text-slate-400/80 dark:text-slate-200',
                label ? 'top-[33px]' : 'top-[14px]',
                passwordInputIconAdditionalClasses,
              )}
              {...getTestProps(testId, 'password-toggle-visible')}
            />
          )}
        </>
      )}
      <div className="relative w-full">
        {type === 'search' && isSearchIcon && (
          <MagnifierIcon
            className="absolute top-3 left-4 z-1 w-4 2xl:w-5 h-5 2xl:h-6 text-blue"
            {...getTestProps(testId, 'search-icon')}
          />
        )}
        {visualSeparateSlash && !isFocused && handleSeparateVisualSlash()}

        <FieldEditorsIndicator editors={editors}>
          <input
            className={twMerge(
              'text-base px-4 relative bg-white',
              'focus:outline-hidden focus:border-blue',
              'border-slate-200 dark:border-slate-800 border rounded-lg',
              'placeholder:text-slate-400 placeholder:font-light',
              'text-ellipsis text-slate-900 dark:text-white',
              'py-1 h-12 w-full dark:bg-transparent',
              handleSeparateVisualSlash() && 'dark:bg-slate-950',
              disabled && 'bg-slate-100 dark:bg-slate-900',
              required && !label && 'pr-5',
              type === 'password' && 'pr-8',
              type === 'search' && isSearchIcon && 'pl-12',
              error ? 'border-red' : '',
              readonly && 'cursor-not-allowed',
              additionalInputClasses,
            )}
            value={currentValue}
            type={currentType}
            name={name}
            style={apiKeyInputStyle as CSSProperties}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            required={required}
            readOnly={readonly}
            disabled={disabled}
            ref={refs || ref}
            {...getTestProps(testId, 'input')}
            {...props}
          />
        </FieldEditorsIndicator>
      </div>
      {type === 'password' && showPasswordInfo && isFocused && (
        <PasswordValidationBox
          password={typeof currentValue === 'string' ? currentValue : ''}
          testId={testId}
        />
      )}
      <HelpErrorTextsTemplate
        helpText={helpText}
        error={error}
        additionalErrorClasses={additionalErrorClasses}
        testId={testId}
      />
    </div>
  );
};

export default Input;
